import { API_FETCH_ABORT_SYMBOL, API_FORBIDDEN_EXCEPTION_ERROR, API_GLOBAL_ERROR } from './symbols';

/**
 * The subset of `ApiWrapperError` this description reads. Declared structurally so
 * the class can import the helper without a cycle.
 */
export interface ApiFailureDescription {
	symbol?: symbol;
	status?: number;
	error?: unknown;
	request?: { method: string; endpoint: string };
}

const HEADLINE_BY_SYMBOL = new Map<symbol, string>([
	[API_FETCH_ABORT_SYMBOL, 'API request aborted'],
	[API_FORBIDDEN_EXCEPTION_ERROR, 'API request forbidden'],
	[API_GLOBAL_ERROR, 'API request failed globally'],
]);

const backendCode = (error: unknown): number | undefined => {
	if (typeof error !== 'object' || error === null || !('code' in error)) {
		return undefined;
	}
	return typeof error.code === 'number' ? error.code : undefined;
};

/**
 * Builds the `message` of an `ApiWrapperError`. Two constraints shape it.
 *
 * It names the call, because Sentry's `dedupeIntegration` runs before `beforeSend` and
 * drops an event whose exception type, value and frames match the one before it. Every
 * ApiWrapperError shares a type and a two-frame stack, so without the endpoint here a
 * 500 on one endpoint would silently swallow the next 500 on a different one.
 *
 * It carries the status and the backend's numeric code but never the backend's message,
 * which routinely embeds ids (docId, instanceId) that would churn the title. The
 * endpoint is safe to include only because `toEndpointShape` has already collapsed
 * id-shaped segments.
 */
export const describeApiFailure = (failure: ApiFailureDescription): string => {
	const headline =
		(failure.symbol && HEADLINE_BY_SYMBOL.get(failure.symbol)) ?? 'API request failed';
	const code = backendCode(failure.error);
	const details = [
		failure.status === undefined ? undefined : String(failure.status),
		code === undefined ? undefined : `code ${code}`,
	].filter((part): part is string => part !== undefined);
	const suffix = details.length > 0 ? ` (${details.join(', ')})` : '';

	if (!failure.request) {
		return `${headline}${suffix}`;
	}
	return `${failure.request.method} ${failure.request.endpoint} — ${headline}${suffix}`;
};
